import React, { useState } from 'react';
import { Button } from '../../components/ui';
import CreditsPicker from '../../components/forms/criteria/CreditsPicker';
import DiscoveryRefused from '../../components/forms/criteria/DiscoveryRefused';
import { PickerComponent } from '../../hooks/useCreditsPicker';
import { DiscoveryRefusal } from '../../types/discovery.types';
import { evaluateCreditGate } from '../../utils/creditGate';
import { REQUEST_PHASES, fixedRequestState, useDemoCreditRequest } from './creditRequestFixtures';
import Section from './Section';

/**
 * /design — the step 2 → 3 exit when the org is short of credits. Fixture
 * data only: a 13-line BOM against a 400 balance at 100 per component (the
 * picker), and a 40 balance (the refused card). Messages are the backend's
 * own sentences for those numbers. Start does nothing here — the connected
 * step wires useDiscoveryExit.
 *
 * Also the step-2 ENTRY gate (the same picker, `variant="gate"`): the same BOM
 * against 1,000 credits (pick 10 of 13), with three lines already paid for,
 * and with 0 credits (nothing to pick). Messages come from `evaluateCreditGate`,
 * the rule the connected step uses. "Take N forward" and "Back to upload" just
 * close it here.
 *
 * "Request more credits" is shown in its four states (idle, pending, just
 * requested, could not send) on the refused card, and the picker's own button
 * plays the real journey locally (see `creditRequestFixtures`).
 */

const COMPONENTS: PickerComponent[] = [
  { componentId: 'C-01', name: 'BLDC hub motor 48V 350W', mpn: 'HM-48350', quantity: 4 },
  { componentId: 'C-02', name: 'Motor controller, FOC', partNumber: 'ACME-MC-220', quantity: 4 },
  { componentId: 'C-03', name: 'Planetary gearbox 20:1', mpn: 'PG42-20', quantity: 4 },
  { componentId: 'C-04', name: 'LiFePO4 battery pack 48V 40Ah', partNumber: 'ACME-BP-4840', quantity: 1 },
  { componentId: 'C-05', name: 'Battery management system', mpn: 'BMS-16S-100', quantity: 1 },
  { componentId: 'C-06', name: '2D LiDAR, 25 m range', mpn: 'LDS-25T', quantity: 2 },
  { componentId: 'C-07', name: 'Safety bumper switch', quantity: 2 },
  { componentId: 'C-08', name: 'Emergency stop button', partNumber: 'ACME-ES-01', quantity: 2 },
  { componentId: 'C-09', name: 'Caster wheel 100 mm', mpn: 'CW-100-PU', quantity: 4 },
  { componentId: 'C-10', name: 'Aluminium extrusion 40×40, 1 m', partNumber: 'ACME-AX-4040', quantity: 12 },
  { componentId: 'C-11', name: 'Industrial PC, fanless', mpn: 'IPC-J6412', quantity: 1 },
  { componentId: 'C-12', name: 'DC-DC converter 48V→24V', mpn: 'DDC-4824-10', quantity: 2 },
  { componentId: 'C-13', name: 'Wiring harness, main', quantity: 1 },
];

const SHORT: DiscoveryRefusal = {
  message:
    "This BOM has 13 components and discovery costs 1,300 credits. Your org has 400, enough for 4, so I haven't started yet. Pick the 4 to start with, or request more credits.",
  components: 13,
  cost: 1300,
  balance: 400,
  affordable: 4,
  rate: 100,
};

const NONE: DiscoveryRefusal = {
  message:
    "This BOM has 13 components and discovery costs 1,300 credits. Your org has 40, not enough for one component, so I haven't started yet. Request more credits, then try again.",
  components: 13,
  cost: 1300,
  balance: 40,
  affordable: 0,
  rate: 100,
};

type GateScenario = { label: string; balance: number; paid: string[] };

const GATE_SCENARIOS: Record<string, GateScenario> = {
  pick: { label: 'Open the entry gate · balance 1,000', balance: 1000, paid: [] },
  paid: { label: 'With 3 lines already paid for · balance 400', balance: 400, paid: ['C-01', 'C-02', 'C-03'] },
  none: { label: 'Nothing affordable · balance 0', balance: 0, paid: [] },
};

const gateFor = ({ balance, paid }: GateScenario) => {
  const gate = evaluateCreditGate({ components: COMPONENTS, paidComponentIds: paid, rate: 100, balance, readOnly: false });
  if (!gate.needed) throw new Error('The gallery scenario no longer needs the gate');
  return gate;
};

const DiscoveryCreditsGallery: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [gateKey, setGateKey] = useState<string | null>(null);
  const gate = gateKey ? gateFor(GATE_SCENARIOS[gateKey]) : null;
  const demoRequest = useDemoCreditRequest();
  return (
    <Section title="Step 2 exit — not enough credits (picker · refused)">
      <div className="mx-auto flex max-w-content flex-col gap-s6">
        <div className="flex flex-wrap items-center gap-s4">
          <Button onClick={() => setOpen(true)}>Open the credits picker</Button>
          <span className="text-body-sm text-ink-muted">13 components · balance 400 · 100 per component</span>
        </div>
        {REQUEST_PHASES.map(({ label, phase }) => (
          <div key={phase} className="flex flex-col gap-s2">
            <span className="text-meta text-ink-muted">Refused card — {label}</span>
            <DiscoveryRefused refusal={NONE} rfqLabel="RFQ-003" creditRequest={fixedRequestState(phase)} />
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-s4">
          {Object.entries(GATE_SCENARIOS).map(([key, scenario]) => (
            <Button key={key} variant="secondary" onClick={() => setGateKey(key)}>
              {scenario.label}
            </Button>
          ))}
        </div>
      </div>
      {gate && (
        <CreditsPicker
          open
          variant="gate"
          refusal={gate.refusal}
          components={COMPONENTS}
          paidComponentIds={gate.paidIds}
          rfqLabel="RFQ-003"
          creditRequest={demoRequest}
          onStart={() => setGateKey(null)}
          onBack={() => setGateKey(null)}
        />
      )}
      <CreditsPicker
        open={open}
        refusal={SHORT}
        components={COMPONENTS}
        rfqLabel="RFQ-003"
        creditRequest={demoRequest}
        onStart={() => setOpen(false)}
        onCancel={() => setOpen(false)}
      />
    </Section>
  );
};

export default DiscoveryCreditsGallery;
